/**
 * GuestbookForm — 방문자 방명록 작성 폼
 */

import { useState } from 'react'
import { SubmitGuestbookEntrySchema } from '@cugumi/shared/schemas'
import type { GuestbookEntryPublic } from '@cugumi/shared/schemas'
import { useGuestbookSubmit } from '@/hooks/useGuestbook.js'
import { Checkbox } from '@/components/ui/index.js'
import styles from '../GuestbookBlock.module.css'

interface GuestbookFormProps {
  invitationId: string
  onSubmitSuccess?: (entry: GuestbookEntryPublic) => void
}

export function GuestbookForm({ invitationId, onSubmitSuccess }: GuestbookFormProps) {
  const [name, setName] = useState('')
  const [message, setMessage] = useState('')
  const [password, setPassword] = useState('')
  const [isPrivate, setIsPrivate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { submit, isLoading, error: apiError } = useGuestbookSubmit(invitationId)

  const handleSubmit = async () => {
    setError(null)

    // 클라이언트 Zod 검증
    const parsed = SubmitGuestbookEntrySchema.safeParse({
      name: name.trim(),
      message: message.trim(),
      password,
      isPrivate,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0].message)
      return
    }

    const result = await submit(parsed.data)
    if (result) {
      // 성공: 폼 초기화 + 콜백
      const optimistic: GuestbookEntryPublic = {
        id: result.id,
        name: name.trim(),
        message: message.trim(),
        createdAt: result.createdAt,
      }
      setName('')
      setMessage('')
      setPassword('')
      setIsPrivate(false)
      onSubmitSuccess?.(optimistic)
    }
    // 실패 시 apiError에 서버 에러 메시지가 자동 설정됨
  }

  return (
    <div className={styles.form}>
      <div className={styles.formRow}>
        <input
          className={styles.formInput}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="이름"
          maxLength={100}
        />
        <input
          className={styles.formInput}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="비밀번호"
          maxLength={20}
        />
      </div>
      <div className={styles.formField}>
        <textarea
          className={styles.formTextarea}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="축하 메시지를 남겨주세요"
          maxLength={500}
          rows={3}
        />
        <div className={styles.formRow}>
          <div className={styles.formCheckRow}>
            <Checkbox
              checked={isPrivate}
              onChange={setIsPrivate}
              label="비공개"
            />
          </div>
          <span className={styles.charCount}>{message.length}/500</span>
        </div>
      </div>
      {(error || apiError) && <p className={styles.formError}>{error || apiError}</p>}
      <button
        type="button"
        className={styles.submitBtn}
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {isLoading ? '등록 중...' : '등록'}
      </button>
    </div>
  )
}
